import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  Loader2,
  Trophy,
  Download,
  Mail,
  Phone,
  MapPin,
  Calendar,
  FileText,
  MessageSquare,
  Euro,
  User as UserIcon,
  Shield,
  Filter,
  X,
} from "lucide-react";


import { supabase } from "@/integrations/supabase/client";
import { crmReport, crmGetClient } from "@/lib/crm.functions";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
// jspdf is loaded on demand (browser only) to keep it out of the SSR bundle.
async function loadPdf() {
  const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);
  return { jsPDF, autoTable };
}

export const Route = createFileRoute("/crm/closed-deals")({
  component: ClosedDealsPage,
});

type WonClient = {
  id: string;
  name: string;
  company_name?: string | null;
  city_id?: string | null;
  assigned_rep?: string | null;
  created_by?: string | null;
  assigned_rep_label?: string | null;
  created_by_label?: string | null;
  email?: string | null;
  phone?: string | null;
  segment?: string | null;
  status?: string | null;
  _won: { count: number; value: number; latest: string | null };
};

const displayNameOf = (c: { company_name?: string | null; name?: string | null }) =>
  (c.company_name && c.company_name.trim()) || c.name || "Client";

const nameSourceOf = (c: { company_name?: string | null; name?: string | null }): "company" | "contact" =>
  c.company_name && c.company_name.trim() ? "company" : "contact";



function fmtDate(d?: string | null) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return "—";
  }
}

function segColor(seg?: string | null) {
  if (seg === "eat_drink") return { tint: "#f97316", label: "Eat & Drink" };
  if (seg === "happenings") return { tint: "#a855f7", label: "Happenings" };
  if (seg === "do") return { tint: "#0ea5e9", label: "To Do" };
  return { tint: "#0d6efd", label: seg ?? "—" };
}

function initialsOf(name: string) {
  return (
    String(name || "?")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join("") || "?"
  );
}

function ClosedDealsPage() {
  const queryClient = useQueryClient();
  const fetchReport = useServerFn(crmReport);
  const reportQ = useQuery({
    queryKey: ["crm", "report", "closed-deals"],
    queryFn: () => fetchReport({ data: {} }),
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  const [openId, setOpenId] = useState<string | null>(null);
  const [repFilter, setRepFilter] = useState<string>("all");
  const [addedByFilter, setAddedByFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  useEffect(() => {
    const channel = supabase
      .channel("crm-closed-deals-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "crm_deals" }, () => {
        queryClient.invalidateQueries({ queryKey: ["crm", "report"] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "crm_clients" }, () => {
        queryClient.invalidateQueries({ queryKey: ["crm", "report"] });
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const cityMap: Record<string, string> = (reportQ.data as any)?.cityMap ?? {};
  const reps: Record<string, { email: string | null; display_name: string | null }> =
    (reportQ.data as any)?.reps ?? {};
  const repMap: Record<string, string> = (reportQ.data as any)?.repMap ?? {};
  const repLabel = (id?: string | null, directLabel?: string | null) => {
    if (directLabel) return directLabel;
    if (!id) return "Not Assigned";
    if (repMap[id]) return repMap[id];
    const rep = reps[id];
    return rep?.display_name || rep?.email || id.slice(0, 8);
  };

  const wonClients: WonClient[] = useMemo(() => {
    const clients: any[] = reportQ.data?.clients ?? [];
    const deals: any[] = reportQ.data?.deals ?? [];
    const wonByClient = new Map<string, { count: number; value: number; latest: string | null }>();
    for (const d of deals) {
      if (d.status !== "won" || !d.client_id) continue;
      const cur = wonByClient.get(d.client_id) ?? { count: 0, value: 0, latest: null };
      cur.count += 1;
      cur.value += Number(d.value_eur ?? 0);
      const when = d.won_at ?? d.updated_at ?? d.created_at;
      if (when && (!cur.latest || when > cur.latest)) cur.latest = when;
      wonByClient.set(d.client_id, cur);
    }
    return clients
      .filter((c) => wonByClient.has(c.id))
      .map((c) => ({ ...c, _won: wonByClient.get(c.id)! }))
      .sort((a, b) => (b._won.latest ?? "").localeCompare(a._won.latest ?? ""));
  }, [reportQ.data]);

  // Build filter option lists
  const repOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of (reportQ.data?.clients ?? []) as any[]) {
      if (c.assigned_rep) m.set(c.assigned_rep, repLabel(c.assigned_rep, c.assigned_rep_label));
    }
    return Array.from(m, ([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportQ.data]);

  const addedByOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of (reportQ.data?.clients ?? []) as any[]) {
      if (c.created_by) m.set(c.created_by, repLabel(c.created_by, c.created_by_label));
    }
    return Array.from(m, ([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportQ.data]);

  const categoryOptions = useMemo(() => {
    const s = new Set<string>();
    for (const c of (reportQ.data?.clients ?? []) as any[]) {
      if (c.segment) s.add(c.segment);
    }
    return Array.from(s).map((seg) => ({ id: seg, label: segColor(seg).label }));
  }, [reportQ.data]);

  const matchesFilters = (c: any) =>
    (repFilter === "all" || c.assigned_rep === repFilter) &&
    (addedByFilter === "all" || c.created_by === addedByFilter) &&
    (categoryFilter === "all" || c.segment === categoryFilter);

  const filteredWon = useMemo(
    () => wonClients.filter(matchesFilters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [wonClients, repFilter, addedByFilter, categoryFilter],
  );

  const totalValue = filteredWon.reduce((s, c) => s + c._won.value, 0);
  const totalDeals = filteredWon.reduce((s, c) => s + c._won.count, 0);
  const activeFilterCount =
    (repFilter !== "all" ? 1 : 0) +
    (addedByFilter !== "all" ? 1 : 0) +
    (categoryFilter !== "all" ? 1 : 0);

  const exportAllPdf = async () => {
    const { jsPDF, autoTable } = await loadPdf();
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Closed Deals — Clients", 14, 18);
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(
      `Generated ${new Date().toLocaleString()}  ·  ${filteredWon.length} clients  ·  ${totalDeals} deals  ·  €${totalValue.toLocaleString()}`,
      14,
      25,
    );
    autoTable(doc, {
      startY: 32,
      head: [["Client", "Source", "City", "Rep", "Deals", "Value", "Won"]],
      body: filteredWon.map((c) => [
        displayNameOf(c),
        nameSourceOf(c) === "company" ? "Company" : "Contact (fallback)",
        (c.city_id && cityMap[c.city_id]) || "—",
        repLabel(c.assigned_rep, c.assigned_rep_label),
        c._won.count,
        `EUR ${c._won.value.toLocaleString()}`,
        fmtDate(c._won.latest),
      ]),
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [22, 163, 74] },
    });
    const fallbackCount = filteredWon.filter((c) => nameSourceOf(c) === "contact").length;
    if (fallbackCount > 0) {
      const endY = (doc as any).lastAutoTable?.finalY ?? 32;
      doc.setFontSize(8);
      doc.setTextColor(120);
      doc.text(
        `Note: ${fallbackCount} row${fallbackCount === 1 ? "" : "s"} show the contact name because no Company Name is set on the client.`,
        14,
        endY + 6,
      );
    }
    doc.save(`closed-deals-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600 text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, white 0, transparent 40%), radial-gradient(circle at 80% 60%, white 0, transparent 35%)",
          }}
        />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-4 pb-8">
          <div className="flex items-center justify-between gap-3">
            <Link
              to="/crm/reports"
              className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 backdrop-blur px-2.5 py-1.5 text-xs font-semibold transition"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Reports
            </Link>
            {filteredWon.length > 0 && (
              <button
                type="button"
                onClick={exportAllPdf}
                className="inline-flex items-center gap-1.5 rounded-lg bg-white text-emerald-700 px-3 py-1.5 text-xs font-bold hover:bg-emerald-50 shadow-md transition"
              >
                <Download className="h-3.5 w-3.5" /> Export PDF
              </button>
            )}
          </div>

          <div className="mt-7 flex items-start gap-4">
            <div className="grid size-14 place-items-center rounded-2xl bg-white/15 backdrop-blur ring-1 ring-white/30 shadow-lg shadow-emerald-900/20">
              <Trophy className="h-7 w-7" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 ring-1 ring-white/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white/90 backdrop-blur">
                <span className="size-1.5 rounded-full bg-emerald-300 animate-pulse" /> Live · Won Pipeline
              </div>
              <h1 className="mt-1.5 text-3xl sm:text-4xl font-black tracking-tight leading-none">
                Closed Deals
              </h1>
              <p className="mt-1.5 text-xs sm:text-sm text-emerald-50/85">
                Won clients and their lifetime value across your team
              </p>
            </div>
          </div>

          {filteredWon.length > 0 && (
            <div className="mt-6 grid grid-cols-3 gap-2 sm:gap-4">
              <HeroStat label="Clients" value={String(filteredWon.length)} />
              <HeroStat label="Won Deals" value={String(totalDeals)} />
              <HeroStat label="Total Value" value={`€${totalValue.toLocaleString()}`} highlight />
            </div>
          )}
        </div>
      </div>

      <main className="mx-auto max-w-6xl px-3 sm:px-4 lg:px-6 -mt-4 pb-10 relative z-10 space-y-6">
        {reportQ.isLoading ? (
          <div className="grid place-items-center py-20 bg-white rounded-2xl shadow-sm">
            <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
          </div>
        ) : (
          <>
            {/* Filter bar */}
            <section className="rounded-2xl bg-white p-3 sm:p-4 shadow-[0_4px_24px_-12px_rgba(15,23,42,0.18)] ring-1 ring-slate-200/70">
              <div className="flex items-center gap-2 mb-2.5">
                <div className="grid size-7 place-items-center rounded-lg bg-emerald-50 ring-1 ring-emerald-100">
                  <Filter className="h-3.5 w-3.5 text-emerald-700" />
                </div>
                <h3 className="text-[13px] font-bold tracking-tight text-slate-900">Filters</h3>
                {activeFilterCount > 0 && (
                  <span className="rounded-full bg-emerald-600 px-1.5 py-0.5 text-[9px] font-bold text-white">
                    {activeFilterCount} active
                  </span>
                )}
                {activeFilterCount > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setRepFilter("all");
                      setAddedByFilter("all");
                      setCategoryFilter("all");
                    }}
                    className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                  >
                    <X className="h-3 w-3" /> Clear
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <FilterSelect
                  label="Sales Rep"
                  value={repFilter}
                  onChange={setRepFilter}
                  options={repOptions}
                  allLabel="All Reps"
                />
                <FilterSelect
                  label="Added By"
                  value={addedByFilter}
                  onChange={setAddedByFilter}
                  options={addedByOptions}
                  allLabel="All Admins / Reps"
                />
                <FilterSelect
                  label="Category"
                  value={categoryFilter}
                  onChange={setCategoryFilter}
                  options={categoryOptions}
                  allLabel="All Categories"
                />
              </div>
            </section>



            {filteredWon.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-white p-16 text-center shadow-sm">
                <Trophy className="mx-auto h-10 w-10 text-emerald-600/50 mb-3" />
                <p className="text-sm font-semibold text-foreground">No closed deals yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Won deals will appear here as your team closes them.
                </p>
              </div>
            ) : (
              <section>
                <div className="mb-3 flex items-end justify-between">
                  <div>
                    <h2 className="text-base font-bold tracking-tight text-slate-900">Won Clients</h2>
                    <p className="text-[11px] text-slate-500">Clients with at least one closed-won deal</p>
                  </div>
                  <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-slate-500 ring-1 ring-slate-200">
                    {filteredWon.length}
                  </span>
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">

            {filteredWon.map((c, idx) => {
              const seg = segColor(c.segment);
              const initials = initialsOf(displayNameOf(c));
              const isTop = idx === 0;
              return (
                <li
                  key={c.id}
                  onClick={() => setOpenId(c.id)}
                  className="group relative overflow-hidden rounded-xl bg-white ring-1 ring-slate-200/70 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-emerald-500/10 hover:ring-emerald-400/50 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
                >
                  {/* Accent bar */}
                  <div
                    className="absolute inset-x-0 top-0 h-0.5"
                    style={{ background: `linear-gradient(90deg, ${seg.tint}, ${seg.tint}55)` }}
                  />

                  <div className="relative p-3">
                    {/* Header row */}
                    <div className="flex items-center gap-2.5">
                      <div
                        className="grid size-9 shrink-0 place-items-center rounded-lg text-[12px] font-bold text-white shadow-sm"
                        style={{ background: `linear-gradient(135deg, ${seg.tint}, ${seg.tint}cc)` }}
                      >
                        {initials}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <h3 className="truncate text-[13px] font-semibold tracking-tight text-slate-900 leading-tight">
                            {displayNameOf(c)}
                          </h3>
                          {isTop && (
                            <Trophy className="h-3 w-3 shrink-0 text-amber-500" />
                          )}
                        </div>
                        <p className="mt-0.5 truncate text-[10px] font-medium text-slate-500">
                          {seg.label} · {(c.city_id && cityMap[c.city_id]) || "—"}
                        </p>
                      </div>
                      <span className="rounded-md bg-slate-50 px-1.5 py-0.5 text-[9px] font-bold tabular-nums text-slate-400 ring-1 ring-slate-100">
                        #{idx + 1}
                      </span>
                    </div>

                    {/* Value row */}
                    <div className="mt-2.5 flex items-baseline justify-between border-t border-slate-100 pt-2.5">
                      <div className="min-w-0">
                        <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 leading-none">
                          Lifetime
                        </div>
                        <div className="mt-1 flex items-baseline gap-0.5">
                          <span className="text-[11px] font-semibold text-emerald-700">€</span>
                          <span className="text-[17px] font-bold tabular-nums text-emerald-700 leading-none tracking-tight">
                            {c._won.value.toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <span className="rounded-md bg-emerald-50 px-1.5 py-1 text-[10px] font-bold tabular-nums text-emerald-700 ring-1 ring-emerald-100">
                        {c._won.count} {c._won.count === 1 ? "deal" : "deals"}
                      </span>
                    </div>

                    {/* Meta */}
                    <dl className="mt-2.5 space-y-1 border-t border-slate-100 pt-2 text-[11px]">
                      <div className="flex items-center gap-2">
                        <UserIcon className="h-3 w-3 shrink-0 text-slate-400" />
                        <dt className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 w-12 shrink-0">Rep</dt>
                        <dd className="truncate font-medium text-slate-700">{repLabel(c.assigned_rep, c.assigned_rep_label)}</dd>
                      </div>
                      <div className="flex items-center gap-2">
                        <Shield className="h-3 w-3 shrink-0 text-slate-400" />
                        <dt className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 w-12 shrink-0">Added</dt>
                        <dd className="truncate font-medium text-slate-700">{repLabel(c.created_by, c.created_by_label)}</dd>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3 w-3 shrink-0 text-slate-400" />
                        <dt className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 w-12 shrink-0">Won</dt>
                        <dd className="truncate font-medium text-slate-700">{fmtDate(c._won.latest)}</dd>
                      </div>
                    </dl>
                  </div>
                </li>
              );
            })}
                </ul>
              </section>
            )}
          </>
        )}
      </main>


      <ClientDetailSheet
        clientId={openId}
        open={!!openId}
        onClose={() => setOpenId(null)}
        cityMap={cityMap}
        repLabel={repLabel}
      />
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
  allLabel,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { id: string; label: string }[];
  allLabel: string;
}) {
  const isActive = value !== "all";
  return (
    <label className="block">
      <span className="block text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">
        {label}
      </span>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full appearance-none rounded-lg border px-2.5 py-2 pr-7 text-[12px] font-medium transition focus:outline-none focus:ring-2 focus:ring-emerald-500/30 ${
            isActive
              ? "border-emerald-400 bg-emerald-50/50 text-emerald-900"
              : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
          }`}
        >
          <option value="all">{allLabel}</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
        <svg
          className="pointer-events-none absolute right-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400"
          viewBox="0 0 12 12"
          fill="none"
        >
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </label>
  );
}

function HeroStat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-2xl backdrop-blur p-3 sm:p-4 ring-1 ${
        highlight
          ? "bg-white text-emerald-700 ring-white/60 shadow-lg"
          : "bg-white/15 text-white ring-white/25"
      }`}
    >
      <div
        className={`text-[10px] font-bold uppercase tracking-wider ${
          highlight ? "text-emerald-600/80" : "text-emerald-50/80"
        }`}
      >
        {label}
      </div>
      <div className="mt-1 text-xl sm:text-2xl font-black tabular-nums truncate">{value}</div>
    </div>
  );
}



function ClientDetailSheet({
  clientId,
  open,
  onClose,
  cityMap,
  repLabel,
}: {
  clientId: string | null;
  open: boolean;
  onClose: () => void;
  cityMap: Record<string, string>;
  repLabel: (id?: string | null, directLabel?: string | null) => string;
}) {
  const getClient = useServerFn(crmGetClient);
  const detailQ = useQuery({
    queryKey: ["crm", "client", clientId],
    queryFn: () => getClient({ data: { id: clientId! } }),
    enabled: !!clientId,
    refetchOnWindowFocus: true,
  });

  const client: any = detailQ.data?.client;
  const contacts: any[] = detailQ.data?.contacts ?? [];
  const deals: any[] = detailQ.data?.deals ?? [];
  const contracts: any[] = detailQ.data?.contracts ?? [];

  const wonDeals = deals.filter((d) => d.status === "won");
  const totalValue = wonDeals.reduce((s, d) => s + Number(d.value_eur ?? 0), 0);
  const contractsValue = contracts.reduce((s, c) => s + Number(c.value_eur ?? 0), 0);
  const seg = client ? segColor(client.segment) : null;
  const cityName = client?.city_id ? cityMap[client.city_id] : null;
  const repName = client ? repLabel(client.assigned_rep, client.assigned_rep_label) : "—";
  const addedByName = client ? repLabel(client.created_by, client.created_by_label) : "—";

  const downloadPdf = async () => {
    if (!client) return;
    const { jsPDF, autoTable } = await loadPdf();
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const M = 40;

    // Cover band
    doc.setFillColor(5, 122, 85);
    doc.rect(0, 0, pageWidth, 110, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("CLOSED DEAL · CLIENT REPORT", M, 32);
    doc.setFontSize(22);
    doc.text(displayNameOf(client as any), M, 60);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const subtitle = [
      seg?.label,
      cityName,
      client.status ? `Status: ${client.status}` : null,
    ]
      .filter(Boolean)
      .join("  ·  ");
    if (subtitle) doc.text(subtitle, M, 78);
    doc.setFontSize(9);
    doc.text(`Generated ${new Date().toLocaleString()}  ·  Name source: ${nameSourceOf(client as any) === "company" ? "Company Name" : "Contact (no company set)"}`, M, 94);

    // Stat tiles
    const tileTop = 130;
    const tileH = 56;
    const tileW = (pageWidth - M * 2 - 16) / 3;
    const tiles = [
      { label: "TOTAL CLOSED VALUE", value: `EUR ${totalValue.toLocaleString()}` },
      { label: "WON DEALS", value: String(wonDeals.length) },
      { label: "CONTRACTS", value: `${contracts.length} · EUR ${contractsValue.toLocaleString()}` },
    ];
    tiles.forEach((t, i) => {
      const x = M + i * (tileW + 8);
      doc.setFillColor(245, 248, 246);
      doc.setDrawColor(220, 230, 224);
      doc.roundedRect(x, tileTop, tileW, tileH, 6, 6, "FD");
      doc.setTextColor(110, 120, 115);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.text(t.label, x + 10, tileTop + 16);
      doc.setTextColor(20, 40, 30);
      doc.setFontSize(13);
      doc.text(t.value, x + 10, tileTop + 38);
    });

    // Contact block
    let y = tileTop + tileH + 24;
    doc.setTextColor(20, 30, 25);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Contact", M, y);
    y += 6;
    doc.setDrawColor(220, 225, 222);
    doc.line(M, y, pageWidth - M, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(60, 70, 65);
    const row = (label: string, value: string) => {
      doc.setFont("helvetica", "bold");
      doc.text(label, M, y);
      doc.setFont("helvetica", "normal");
      doc.text(value, M + 90, y);
      y += 14;
    };
    row("City", cityName || "—");
    row("Rep", repName);
    row("Added by", addedByName);
    if (client.email) row("Email", String(client.email));
    if (client.phone) row("Phone", String(client.phone));
    if (client.segment) row("Category", seg?.label || client.segment);
    if (client.notes) {
      y += 4;
      doc.setFont("helvetica", "bold");
      doc.text("Notes", M, y);
      y += 12;
      doc.setFont("helvetica", "normal");
      const wrapped = doc.splitTextToSize(String(client.notes), pageWidth - M * 2);
      doc.text(wrapped, M, y);
      y += wrapped.length * 12;
    }
    y += 16;

    // Deals
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(20, 30, 25);
    doc.text(`Deals (${deals.length})`, M, y);
    autoTable(doc, {
      startY: y + 6,
      head: [["Title", "Value (EUR)", "Status", "Date"]],
      body: deals.length
        ? deals.map((d) => [
            d.title || "—",
            Number(d.value_eur ?? 0).toLocaleString(),
            d.status || "—",
            fmtDate(d.won_at ?? d.updated_at ?? d.created_at),
          ])
        : [["No deals.", "", "", ""]],
      margin: { left: M, right: M },
      styles: { fontSize: 9, cellPadding: 5, textColor: [40, 50, 45] },
      headStyles: { fillColor: [5, 122, 85], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [248, 250, 249] },
    });

    // Contracts
    if (contracts.length > 0) {
      const afterDeals = (doc as any).lastAutoTable?.finalY ?? y;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(20, 30, 25);
      doc.text(`Contracts (${contracts.length})`, M, afterDeals + 22);
      autoTable(doc, {
        startY: afterDeals + 28,
        head: [["Title", "Value (EUR)", "Status", "Signed"]],
        body: contracts.map((c) => [
          c.title || "—",
          Number(c.value_eur ?? 0).toLocaleString(),
          c.status || "—",
          fmtDate(c.signed_at ?? c.created_at),
        ]),
        margin: { left: M, right: M },
        styles: { fontSize: 9, cellPadding: 5, textColor: [40, 50, 45] },
        headStyles: { fillColor: [99, 67, 199], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [249, 248, 253] },
      });
    }

    // Full activity log (no truncation)
    const beforeLog = (doc as any).lastAutoTable?.finalY ?? y;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(20, 30, 25);
    doc.text(`Activity Log (${contacts.length})`, M, beforeLog + 22);
    autoTable(doc, {
      startY: beforeLog + 28,
      head: [["Date", "Channel", "Message / Summary"]],
      body: contacts.length
        ? contacts.map((c) => [
            new Date(c.occurred_at).toLocaleString(),
            String(c.channel || "—"),
            String(c.summary || ""),
          ])
        : [["—", "—", "No activity recorded."]],
      margin: { left: M, right: M },
      styles: { fontSize: 9, cellPadding: 5, valign: "top", textColor: [40, 50, 45] },
      headStyles: { fillColor: [13, 110, 253], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [247, 250, 254] },
      columnStyles: {
        0: { cellWidth: 95 },
        1: { cellWidth: 65 },
        2: { cellWidth: "auto" },
      },
    });

    // Footer with page numbers
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(140, 150, 145);
      doc.text(`${displayNameOf(client as any)} · Closed Deals Report`, M, pageHeight - 18);
      doc.text(`Page ${i} / ${pageCount}`, pageWidth - M, pageHeight - 18, { align: "right" });
    }

    const safe = String(displayNameOf(client as any))
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    doc.save(`${safe}-closed-deals.pdf`);
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto bg-slate-50 p-0">
        {detailQ.isLoading ? (
          <div className="grid min-h-[60vh] place-items-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !client ? (
          <div className="p-8">
            <SheetHeader>
              <SheetTitle>Client</SheetTitle>
            </SheetHeader>
            <p className="text-sm text-muted-foreground py-8">Client not found.</p>
          </div>
        ) : (
          <>
            {/* Hero header */}
            <div
              className="relative overflow-hidden px-5 pt-6 pb-20 text-white"
              style={{
                background:
                  "linear-gradient(135deg, #047857 0%, #059669 45%, #0d9488 100%)",
              }}
            >
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-20"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 15% 20%, white 0, transparent 45%), radial-gradient(circle at 85% 80%, white 0, transparent 40%)",
                }}
              />
              <SheetHeader className="relative">
                <SheetTitle className="text-white text-left flex items-center gap-2">
                  <Trophy className="h-4 w-4" /> Closed Client
                </SheetTitle>
              </SheetHeader>
              <div className="relative mt-4 flex items-start gap-3">
                <div
                  className="grid size-14 shrink-0 place-items-center rounded-2xl text-base font-black ring-2 ring-white/40 shadow-lg"
                  style={{
                    background: `linear-gradient(135deg, ${seg!.tint}, ${seg!.tint}cc)`,
                  }}
                >
                  {initialsOf(displayNameOf(client as any))}
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="truncate text-xl font-black leading-tight">{displayNameOf(client as any)}</h2>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <span className="rounded-full bg-white/20 backdrop-blur px-2 py-0.5 text-[10px] font-bold ring-1 ring-white/30">
                      {seg!.label}
                    </span>
                    {cityName && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-white/15 backdrop-blur px-2 py-0.5 text-[10px] font-semibold ring-1 ring-white/25">
                        <MapPin className="h-2.5 w-2.5" /> {cityName}
                      </span>
                    )}
                    {client.status && (
                      <span className="rounded-full bg-white/10 backdrop-blur px-2 py-0.5 text-[10px] font-semibold ring-1 ring-white/20 capitalize">
                        {client.status}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="relative -mt-14 px-4 pb-6 space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <StatTile label="Closed Value" value={`€${totalValue.toLocaleString()}`} accent="emerald" />
                <StatTile label="Won Deals" value={String(wonDeals.length)} accent="slate" />
                <StatTile label="Contracts" value={String(contracts.length)} accent="indigo" />
              </div>

              <section className="rounded-2xl bg-white p-4 shadow-[0_4px_24px_-12px_rgba(15,23,42,0.18)] ring-1 ring-slate-200/70">
                <h3 className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  <UserIcon className="h-3.5 w-3.5" /> Contact
                </h3>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5 text-[12px]">
                  <DetailRow icon={MapPin} label="City" value={cityName || "—"} />
                  <DetailRow icon={UserIcon} label="Rep" value={repName} />
                  <DetailRow icon={Shield} label="Added By" value={addedByName} />
                  {client.email && (
                    <DetailRow icon={Mail} label="Email" value={client.email} href={`mailto:${client.email}`} />
                  )}
                  {client.phone && (
                    <DetailRow icon={Phone} label="Phone" value={client.phone} href={`tel:${client.phone}`} />
                  )}
                </dl>
                {client.notes && (
                  <p className="mt-3 whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-[12px] text-slate-700 ring-1 ring-slate-100">
                    {client.notes}
                  </p>
                )}
              </section>

              <SectionCard title="Deals" count={deals.length} icon={Euro} accent="emerald">
                {deals.length === 0 ? (
                  <EmptyRow text="No deals." />
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {deals.map((d) => (
                      <li key={d.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[13px] font-semibold text-slate-900">{d.title || "—"}</div>
                          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-500">
                            <StatusDot status={d.status} />
                            <span className="capitalize">{d.status}</span>
                            <span>·</span>
                            <Calendar className="h-2.5 w-2.5" />
                            <span>{fmtDate(d.won_at ?? d.updated_at ?? d.created_at)}</span>
                          </div>
                        </div>
                        <div
                          className={`shrink-0 text-sm font-black tabular-nums ${
                            d.status === "won" ? "text-emerald-700" : "text-slate-400"
                          }`}
                        >
                          €{Number(d.value_eur ?? 0).toLocaleString()}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>

              {contracts.length > 0 && (
                <SectionCard title="Contracts" count={contracts.length} icon={FileText} accent="indigo">
                  <ul className="divide-y divide-slate-100">
                    {contracts.map((c) => (
                      <li key={c.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[13px] font-semibold text-slate-900">{c.title || "—"}</div>
                          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-500">
                            <span className="capitalize">{c.status || "—"}</span>
                            <span>·</span>
                            <Calendar className="h-2.5 w-2.5" />
                            <span>{fmtDate(c.signed_at ?? c.created_at)}</span>
                          </div>
                        </div>
                        <div className="shrink-0 text-sm font-black tabular-nums text-indigo-700">
                          €{Number(c.value_eur ?? 0).toLocaleString()}
                        </div>
                      </li>
                    ))}
                  </ul>
                </SectionCard>
              )}

              <SectionCard title="Activity Log" count={contacts.length} icon={MessageSquare} accent="blue">
                {contacts.length === 0 ? (
                  <EmptyRow text="No activity recorded." />
                ) : (
                  <ul className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                    {contacts.map((c) => (
                      <li key={c.id} className="px-3 py-2.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-700 ring-1 ring-blue-100">
                            {c.channel || "note"}
                          </span>
                          <span className="inline-flex items-center gap-1 text-[10px] text-slate-500">
                            <Calendar className="h-2.5 w-2.5" />
                            {new Date(c.occurred_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="mt-1.5 whitespace-pre-wrap text-[12px] leading-relaxed text-slate-800">
                          {c.summary}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>

              <button
                type="button"
                onClick={downloadPdf}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white px-4 py-3 text-sm font-bold shadow-lg shadow-emerald-600/20 transition active:scale-[0.98]"
              >
                <Download className="h-4 w-4" /> Download Full PDF Report
              </button>
              <p className="text-center text-[10px] text-slate-400">
                Includes contact, deals, contracts and the complete activity log.
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function StatTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: "emerald" | "slate" | "indigo";
}) {
  const isGradient = accent !== "slate";
  const cls =
    accent === "emerald"
      ? "bg-gradient-to-br from-emerald-500 to-emerald-600 text-white ring-emerald-300/40"
      : accent === "indigo"
        ? "bg-gradient-to-br from-indigo-500 to-indigo-600 text-white ring-indigo-300/40"
        : "bg-white text-slate-900 ring-slate-200";
  return (
    <div className={`rounded-xl p-3 shadow-[0_4px_16px_-8px_rgba(15,23,42,0.25)] ring-1 ${cls}`}>
      <div
        className={`text-[9px] font-bold uppercase tracking-wider ${
          isGradient ? "text-white/80" : "text-slate-500"
        }`}
      >
        {label}
      </div>
      <div className="mt-1 text-base font-black tabular-nums leading-tight truncate">{value}</div>
    </div>
  );
}

function SectionCard({
  title,
  count,
  icon: Icon,
  accent,
  children,
}: {
  title: string;
  count: number;
  icon: any;
  accent: "emerald" | "indigo" | "blue";
  children: React.ReactNode;
}) {
  const a =
    accent === "emerald"
      ? { bg: "bg-emerald-50/60", text: "text-emerald-700", ring: "ring-emerald-100" }
      : accent === "indigo"
        ? { bg: "bg-indigo-50/60", text: "text-indigo-700", ring: "ring-indigo-100" }
        : { bg: "bg-blue-50/60", text: "text-blue-700", ring: "ring-blue-100" };
  return (
    <section className="rounded-2xl bg-white shadow-[0_4px_24px_-12px_rgba(15,23,42,0.18)] ring-1 ring-slate-200/70 overflow-hidden">
      <header className={`flex items-center justify-between px-3 py-2 border-b border-slate-100 ${a.bg}`}>
        <h3 className={`inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider ${a.text}`}>
          <Icon className="h-3.5 w-3.5" /> {title}
        </h3>
        <span className={`rounded-full bg-white px-2 py-0.5 text-[10px] font-bold tabular-nums ${a.text} ring-1 ${a.ring}`}>
          {count}
        </span>
      </header>
      {children}
    </section>
  );
}

function DetailRow({
  icon: Icon,
  label,
  value,
  href,
}: {
  icon: any;
  label: string;
  value: string;
  href?: string;
}) {
  const valEl = href ? (
    <a href={href} className="truncate font-semibold text-slate-900 hover:text-emerald-700 hover:underline">
      {value}
    </a>
  ) : (
    <span className="truncate font-semibold text-slate-900">{value}</span>
  );
  return (
    <div className="flex items-start gap-2 min-w-0">
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
      <div className="min-w-0 flex-1">
        <dt className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{label}</dt>
        <dd className="mt-0.5 truncate">{valEl}</dd>
      </div>
    </div>
  );
}

function StatusDot({ status }: { status: string }) {
  const color =
    status === "won" ? "bg-emerald-500" : status === "lost" ? "bg-rose-500" : "bg-amber-500";
  return <span className={`inline-block size-1.5 rounded-full ${color}`} />;
}

function EmptyRow({ text }: { text: string }) {
  return <p className="px-3 py-4 text-center text-[12px] text-slate-400">{text}</p>;
}
